import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getCurrentUser } from '../../data/mockData';
import AccountSidebar from '../../components/AccountSidebar';
import { request } from '../../utils/request';
import { 
  HeartIcon,
  TrashIcon,
  ShareIcon,
  EyeIcon,
  StarIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';

// Types
interface LikedDesign {
  id: number;
  name: string;
  category: string;
  designer: string;
  likedDate: string;
  image: string;
  price: number;
  rating: number;
  reviews: number;
  isPublic: boolean;
  code?: string;
}

interface LikesResponse {
  success: boolean;
  code: number;
  message: string;
  result: {
    likes: LikedDesign[];
    pagination: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };
  };
}

interface CategoriesResponse {
  success: boolean;
  code: number;
  message: string;
  data: string[];
}

const Likes: React.FC = () => {
  const [user] = useState(getCurrentUser());
  const [likedDesigns, setLikedDesigns] = useState<LikedDesign[]>([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null); 
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 0
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    loadLikedDesigns(); 
  }, [selectedCategory]);

  const loadLikedDesigns = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString()
      });
      
      if (selectedCategory !== 'all') {
        params.append('category', selectedCategory);
      }

      const response = await request({
        method: 'GET',
        url: `likes?${params.toString()}`
      }) as LikesResponse;

      if (response.success) {
        setLikedDesigns(response.result.likes);
        setPagination(response.result.pagination);
      } else {
        setError(response.message || 'Failed to load liked designs');
      }
    } catch (err) {
      console.error('Error loading liked designs:', err);
      setError('Failed to load liked designs');
    } finally {
      setIsLoading(false);
    }
  };

   

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Please log in</h2>
          <Link to="/" className="hover:opacity-80" style={{ color: '#2e3a47' }}>
            Back to home
          </Link>
        </div>
      </div>
    );
  }

  const categories = ['all', ...Array.from(new Set(likedDesigns.map(d => d.category)))];
  
  const filteredDesigns = selectedCategory === 'all' 
    ? likedDesigns 
    : likedDesigns.filter(design => design.category === selectedCategory);

  const handleUnlike = async (design: LikedDesign) => {
    try {
      console.log('design', design);
      // Call API to unlike the product
      const response: any = await request({
        url: `likes/${design.code || design.id}`,
        method: 'DELETE'
      });
      
      if (response.success) {
        // Update local state
        setLikedDesigns(likedDesigns.filter(item => item.id !== design.id));
      } else {
        alert(response.message || 'Failed to unlike product');
      }
    } catch (error: any) {
      console.error('Unlike product error:', error);
      alert('Failed to unlike product');
    }
  };

  const handleTogglePublic = async (design: LikedDesign) => {
    try {
      // Call API to toggle public status
      const response: any = await request({
        url: `likes/${design.id}/toggle-public`,
        method: 'PUT'
      });
      
      if (response.success) {
        // Update local state
        setLikedDesigns(likedDesigns.map(item => 
          item.id === design.id 
            ? { ...item, isPublic: !item.isPublic }
            : item
        ));
      } else {
        alert(response.message || 'Failed to update visibility');
      }
    } catch (error: any) {
      console.error('Toggle public status error:', error);
      alert('Failed to update visibility');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Likes</h1>
          <p className="text-gray-600">Your favorite designs and inspirations</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="hidden lg:block lg:col-span-1">
            <AccountSidebar currentPage="likes" />
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Filters and Toolbar */}
            <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <span className="text-sm text-gray-600">
                    {filteredDesigns.length} design{filteredDesigns.length !== 1 ? 's' : ''} liked
                  </span>
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent text-sm"
                    style={{ '--tw-ring-color': '#2e3a47' } as React.CSSProperties}
                  >
                    {categories.map(category => (
                      <option key={category} value={category}>
                        {category === 'all' ? 'All Categories' : category}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 rounded-lg ${
                      viewMode === 'grid' ? '' : 'text-gray-400 hover:text-gray-600'
                    }`}
                    style={viewMode === 'grid' ? { backgroundColor: '#f0f2f5', color: '#2e3a47' } : {}}
                  >
                    <span className="text-lg">⊞</span>
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 rounded-lg ${
                      viewMode === 'list' ? '' : 'text-gray-400 hover:text-gray-600'
                    }`}
                    style={viewMode === 'list' ? { backgroundColor: '#f0f2f5', color: '#2e3a47' } : {}}
                  >
                    <span className="text-lg">☰</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Designs Grid/List */}
            {viewMode === 'grid' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredDesigns.map((design) => (
                  <div key={design.id} className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                    <div className="aspect-square bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center relative">
                    <img src={design.image} alt={design.name} className="w-full h-full object-contain" /> 
                      <div className="absolute top-2 right-2 flex space-x-1">
                        <button className="w-8 h-8 bg-white rounded-full shadow-md flex items-center justify-center hover:bg-gray-50">
                          <ShareIcon className="w-4 h-4 text-gray-600" />
                        </button>
                        <button 
                          onClick={() => handleUnlike(design)}
                          className="w-8 h-8 bg-white rounded-full shadow-md flex items-center justify-center hover:bg-red-50"
                        >
                          <HeartIcon className="w-4 h-4 text-red-600 fill-current" />
                        </button>
                      </div>
                    </div>
                    <div className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-semibold text-gray-900 text-sm line-clamp-2">
                          {design.name}
                        </h3>
                        <span className="text-xs text-gray-500 ml-2">{design.category}</span>
                      </div>
                      <p className="text-xs text-gray-500 mb-2">by {design.designer}</p>
                      <p className="text-xs text-gray-500 mb-3">
                        Liked on {new Date(design.likedDate).toLocaleDateString()}
                      </p>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-1">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <StarIconSolid key={star} className="w-3 h-3 text-yellow-400" />
                          ))}
                          <span className="text-xs text-gray-500 ml-1">({design.rating})</span>
                        </div>
                        <span className="text-sm font-bold" style={{ color: '#2e3a47' }}>${design.price}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">{design.reviews} reviews</span>
                        <button
                          onClick={() => handleTogglePublic(design)}
                          className={`px-2 py-1 text-xs rounded-full ${
                            design.isPublic 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {design.isPublic ? 'Public' : 'Private'}
                        </button>
                      </div>
                      <div className="mt-3 space-y-2">
                        <Link
                          to={`/product/${design.id}`}
                          className="w-full text-white py-2 px-3 rounded-lg transition-colors text-center block text-sm"
                          style={{ backgroundColor: '#2e3a47' }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3a4a57'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2e3a47'}
                        >
                          View Product
                        </Link>
                        {/* <Link
                          to={`/customize/${design.id}/${design.code}`}
                          className="w-full border border-gray-300 text-gray-700 py-2 px-3 rounded-lg hover:bg-gray-50 transition-colors text-center block text-sm"
                        >
                          Customize {JSON.stringify(design)}
                        </Link> */}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                {filteredDesigns.map((design) => (
                  <div key={design.id} className="bg-white rounded-lg shadow-sm p-4">
                    <div className="flex items-center space-x-4">
                      <div className="w-16 h-16 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
                        <span className="text-2xl">{design.image}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="font-semibold text-gray-900 mb-1">{design.name}</h3>
                            <p className="text-sm text-gray-500 mb-2">
                              by {design.designer} • {design.category} • Liked on {new Date(design.likedDate).toLocaleDateString()}
                            </p>
                            <div className="flex items-center space-x-4 text-sm text-gray-500 mb-2">
                              <div className="flex items-center space-x-1">
                                {[1, 2, 3, 4, 5].map((star) => (
                                  <StarIconSolid key={star} className="w-4 h-4 text-yellow-400" />
                                ))}
                                <span>({design.rating})</span>
                              </div>
                              <span>{design.reviews} reviews</span>
                            </div>
                            <div className="flex items-center space-x-4 text-sm text-gray-500">
                              <span className="flex items-center">
                                <EyeIcon className="w-4 h-4 mr-1" />
                                View Product
                              </span>
                              <span className="flex items-center">
                                <span className="w-4 h-4 mr-1">✏️</span>
                                Customize
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="text-lg font-bold" style={{ color: '#2e3a47' }}>${design.price}</span>
                            <button
                              onClick={() => handleTogglePublic(design)}
                              className={`px-3 py-1 text-sm rounded-full ${
                                design.isPublic 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-gray-100 text-gray-600'
                              }`}
                            >
                              {design.isPublic ? 'Public' : 'Private'}
                            </button>
                            <button className="p-2 text-gray-400 hover:text-gray-600">
                              <ShareIcon className="w-4 h-4" />
                            </button>
                            <button 
                              onClick={() => handleUnlike(design)}
                              className="p-2 text-red-400 hover:text-red-600"
                            >
                              <HeartIcon className="w-4 h-4 fill-current" />
                            </button>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2 mt-3">
                          <Link
                            to={`/product/${design.id}`}
                            className="text-white py-2 px-4 rounded-lg transition-colors text-sm"
                            style={{ backgroundColor: '#2e3a47' }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3a4a57'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2e3a47'}
                          >
                            View Product
                          </Link>
                          <Link
                            to={`/customize/${design.id}`}
                            className="border border-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                          >
                            Customize
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {filteredDesigns.length === 0 && (
              <div className="bg-white rounded-lg shadow-sm p-12 text-center">
                <div className="flex justify-center mb-4">
                  <HeartIcon className="w-16 h-16 text-gray-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No liked designs</h3>
                <p className="text-gray-600 mb-6">
                  {selectedCategory === 'all' 
                    ? "You haven't liked any designs yet." 
                    : `No liked designs in ${selectedCategory} category.`}
                </p>
                <Link
                  to="/products"
                  className="inline-flex items-center px-6 py-3 text-white rounded-lg transition-colors"
                  style={{ backgroundColor: '#2e3a47' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3a4a57'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2e3a47'}
                >
                  Browse Designs
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Likes; 